import { useCallback, useEffect, useRef, useState } from 'react';
import { toApiError } from './errors';
import type { ApiError } from './errors';

export type AsyncState<T> =
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  | { status: 'error'; error: ApiError };

export type AsyncResult<T> = AsyncState<T> & { reload: () => void };

/**
 * Runs a request when `key` changes and cancels the previous one, so a slow
 * answer cannot overwrite a fast one.
 *
 * `key` identifies the request — `'tenants'`, `` `tenant:${id}` ``. Loading is
 * derived from a result that does not match the current key rather than
 * written by the effect, which keeps the render pass free of cascades.
 */
export function useAsync<T>(
  run: (signal: AbortSignal) => Promise<T>,
  key: string,
): AsyncResult<T> {
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{ token: string; state: AsyncState<T> } | null>(null);

  const token = `${key}#${String(attempt)}`;

  // Keep the latest callback without making it a dependency: callers pass an
  // inline lambda, and refetching on every render is not what they mean.
  const runRef = useRef(run);
  useEffect(() => {
    runRef.current = run;
  });

  useEffect(() => {
    const controller = new AbortController();

    runRef.current(controller.signal).then(
      (value) => {
        if (!controller.signal.aborted) setResult({ token, state: { status: 'ready', value } });
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setResult({ token, state: { status: 'error', error: toApiError(error) } });
        }
      },
    );

    return () => {
      controller.abort();
    };
  }, [token]);

  const reload = useCallback(() => {
    setAttempt((n) => n + 1);
  }, []);

  const state: AsyncState<T> =
    result !== null && result.token === token ? result.state : { status: 'loading' };

  return { ...state, reload };
}
